import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Accordion from "../Containers/Accordion";
import CreditsUsageBreakdownTables from "./CreditsUsageBreakdownTables";
import { useTranslationApp } from "../../lib/i18next";
import { useDispatchApp } from "../../lib/redux";
import { startGetSuperAdminDetailedCreditsUsage } from "../../actions/payments";
import {
  MODAL_CREDITS_USAGE_DETAILED_PERIOD,
  MODAL_CREDITS_USAGE_DETAILED_SUPER_ADMIN_TOTAL_CREDITS,
  MODAL_CREDITS_USAGE_DETAILED_SUPER_ADMIN_GROUP_CREDITS,
  MODAL_CREDITS_USAGE_DETAILED_SUPER_ADMIN_GROUP_ACCORDION_TITLE,
  MODAL_CREDITS_USAGE_DETAILED_SUPER_ADMIN_EMPTY_GROUPS,
  MODAL_CREDITS_USAGE_DETAILED_SUPER_ADMIN_CALCULATING,
} from "../../i18n/keysTranslations";

const CreditsUsageDetailedSuperAdminContent = ({ modalOpen = false }) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();

  const [loading, setLoading] = useState(false);
  const [totalCredits, setTotalCredits] = useState(0);
  const [byAgency, setByAgency] = useState([]);

  useEffect(() => {
    if (!modalOpen) return;

    let isCancelled = false;

    const onFetchData = async () => {
      setLoading(true);
      setTotalCredits(0);
      setByAgency([]);

      const result = await dispatch(startGetSuperAdminDetailedCreditsUsage());

      if (isCancelled) {
        return;
      }

      if (result?.ok) {
        setTotalCredits(result.totalCredits ?? 0);
        setByAgency(result.byAgency ?? []);
      }
      setLoading(false);
    };

    onFetchData();

    return () => {
      isCancelled = true;
    };
  }, [modalOpen, dispatch]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: 2,
          py: 6,
        }}
      >
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary">
          {t(MODAL_CREDITS_USAGE_DETAILED_SUPER_ADMIN_CALCULATING)}
        </Typography>
      </Box>
    );
  }

  if (byAgency.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t(MODAL_CREDITS_USAGE_DETAILED_SUPER_ADMIN_EMPTY_GROUPS)}
      </Typography>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pb: 1 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        {t(MODAL_CREDITS_USAGE_DETAILED_SUPER_ADMIN_TOTAL_CREDITS, {
          value: totalCredits,
        })}
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, mt: -1 }}>
        {byAgency.map((agency) => (
          <Typography key={agency.agencyId} variant="body2">
            {t(MODAL_CREDITS_USAGE_DETAILED_SUPER_ADMIN_GROUP_CREDITS, {
              agencyName: agency.agencyName,
              value: agency.totalCredits ?? 0,
            })}
          </Typography>
        ))}
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {byAgency.map((agency) => (
          <Accordion
            key={agency.agencyId}
            title={t(
              MODAL_CREDITS_USAGE_DETAILED_SUPER_ADMIN_GROUP_ACCORDION_TITLE,
              { agencyName: agency.agencyName }
            )}
            titleAlign="left"
            variantTitle="subtitle1"
            sxTitle={{ fontWeight: 600 }}
            initialValue={false}
          >
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
            >
              {agency.period && (
                <Typography variant="body2" color="text.secondary">
                  {t(MODAL_CREDITS_USAGE_DETAILED_PERIOD, {
                    from: agency.period.from,
                    to: agency.period.to,
                  })}
                </Typography>
              )}
              <CreditsUsageBreakdownTables
                loading={false}
                byAdvertiser={agency.byAdvertiser ?? []}
                byUser={agency.byUser ?? []}
              />
            </Box>
          </Accordion>
        ))}
      </Box>
    </Box>
  );
};

export default CreditsUsageDetailedSuperAdminContent;
